import { can as podeShared, type MeResponse, type Principal } from '@ifretes/shared'
import { create } from 'zustand'

import { api, onSessaoPerdida, setAccessToken } from '../../lib/api'
import { aplicarTemaDoServidor } from '../../theme/ThemeProvider'

/**
 * Estado de autenticacao.
 *
 * `pode()` usa o MESMO avaliador da API (packages/shared). Aqui ele serve para
 * esconder acao que o usuario nao pode executar — conveniencia visual, NUNCA
 * controle de acesso: quem decide e o guard do servidor (SPEC 5.4).
 */

interface AuthState {
  usuario: MeResponse | null
  /** true enquanto o bootstrap inicial nao terminou — evita piscar a tela de login. */
  carregando: boolean
  /**
   * Passe GPlanSiS nesta carga da pagina: 'pendente' (ainda nao tentou),
   * 'tentando' ou 'feito'. Uma tentativa por carga, nunca em laco.
   */
  passe: 'pendente' | 'tentando' | 'feito'
  tentarPasse: () => Promise<void>
  entrar: (email: string, password: string, totp?: string) => Promise<void>
  sair: () => Promise<void>
  bootstrap: () => Promise<void>
  definirUsuario: (u: MeResponse | null) => void
  pode: (permissao: string) => boolean
  temPapel: (papel: string) => boolean
}

function principalDe(u: MeResponse | null): Principal | null {
  if (!u) return null
  return { userId: u.id, permissions: u.permissions, roles: u.roles, orgIds: u.orgIds }
}

export const useAuth = create<AuthState>((set, get) => ({
  usuario: null,
  carregando: true,
  passe: 'pendente',

  definirUsuario: (usuario) => set({ usuario }),

  entrar: async (email, password, totp) => {
    const r = await api.entrar({ email, password, ...(totp ? { totp } : {}) })
    setAccessToken(r.accessToken)
    aplicarTemaDoServidor(r.user.preferences.theme)
    set({ usuario: r.user, carregando: false })
  },

  sair: async () => {
    try {
      await api.sair()
    } catch {
      // Falha de rede no logout nao pode prender o usuario na sessao: o estado
      // local e limpo de todo jeito, e o refresh token expira sozinho.
    }
    setAccessToken(null)
    // Quem saiu nao e reaberto pelo passe nesta carga (o servidor ainda grava
    // gps_passe_off para as proximas).
    set({ usuario: null, passe: 'feito' })
  },

  /**
   * Passe GPlanSiS na ENTRADA do sistema (tela "Entrar" ou rota protegida), nunca
   * na pagina publica. Sem sessao propria, o `/auth/refresh` com o cabecalho do
   * passe abre a mesma sessao do login; recusou → segue a tela de login normal.
   */
  tentarPasse: async () => {
    if (get().passe !== 'pendente' || get().usuario) return
    set({ passe: 'tentando' })
    try {
      const r = await api.entrarPeloPasse()
      setAccessToken(r.accessToken)
      aplicarTemaDoServidor(r.user.preferences.theme)
      set({ usuario: r.user, carregando: false, passe: 'feito' })
    } catch {
      set({ passe: 'feito' })
    }
  },

  /**
   * Reconstitui a sessao na abertura do app. O access token vive em memoria,
   * entao um F5 sempre chega aqui sem token — o cookie httpOnly e que diz se
   * ainda ha sessao.
   */
  bootstrap: async () => {
    try {
      const r = await api.bootstrap()
      setAccessToken(r.accessToken)
      aplicarTemaDoServidor(r.user.preferences.theme)
      set({ usuario: r.user, carregando: false })
    } catch {
      // Sem cookie valido: visitante anonimo. Nao e erro.
      setAccessToken(null)
      set({ usuario: null, carregando: false })
    }
  },

  pode: (permissao) => podeShared(principalDe(get().usuario), permissao),
  temPapel: (papel) => get().usuario?.roles.includes(papel) ?? false,
}))

// Sessao perdida de forma irrecuperavel (refresh recusado, familia revogada por
// suspeita de roubo): limpa o estado para a UI cair na tela de login.
onSessaoPerdida(() => {
  useAuth.setState({ usuario: null, carregando: false })
})
